import { z } from 'zod';
import { RecurringInvoiceDtoSchema } from './recurring';
import { MilestoneDtoSchema } from './milestones';
/**
 * Reagendar lo que aparece en Facturación → Pendientes (29-10-2026, Jesús: «aquí estaría bien poder reagendar las facturas
 * pendientes»). Dos casos, un endpoint por cada modelo:
 *  - un HITO de pago (`POST /milestones/:id/reschedule`): cambia su fecha de vencimiento;
 *  - una CUOTA de una iguala (`POST /recurring-invoices/:id/reschedule`): mueve la fecha de la próxima emisión de la iguala. ☠️ Solo
 *    se aplaza (la nueva fecha es posterior) y las cuotas programadas ANTES de la nueva fecha dejan de facturarse: es lo que se hizo
 *    a mano el 27-10 con las iguales ADaaS que esperaban a que el cliente pasara a producción.
 */
/** Una fecha de calendario real (YYYY-MM-DD, sin «31 de febrero») entre 2000 y 2100. */
export const DateOnlySchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .refine((s) => {
    const d = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s && s >= '2000-01-01' && s <= '2100-12-31';
}, 'Fecha no válida');
/**
 * El motivo: una línea de texto. ☠️ Se quitan los caracteres de control (un NUL, \u0000, hace fallar el insert de la auditoría y el
 * reagendado quedaba sin rastro); lo que quede en blanco es «sin motivo».
 */
export const RescheduleReasonSchema = z
    .string()
    .max(300)
    // eslint-disable-next-line no-control-regex
    .transform((s) => s.replace(/[\u0000-\u001F\u007F]/g, ' ').replace(/\s+/g, ' ').trim())
    .transform((s) => (s === '' ? undefined : s))
    .optional();
export const RescheduleMilestoneSchema = z.object({
    /** La nueva fecha de vencimiento. */
    dueDate: DateOnlySchema,
    /** La fecha de vencimiento que quien llama cree que tiene ahora: si otra persona ya la cambió, el servidor rechaza (409). */
    expectedDueDate: DateOnlySchema,
    /** Para qué se aplaza (queda en la auditoría). */
    reason: RescheduleReasonSchema,
});
export const RescheduleMilestoneResponseSchema = z.object({ milestone: MilestoneDtoSchema });
export const RescheduleRecurringSchema = z.object({
    /** La nueva fecha de la próxima emisión de la iguala (posterior a la actual). */
    nextRunDate: DateOnlySchema,
    /** La próxima emisión que quien llama cree que tiene la iguala: si otra persona ya la facturó o la movió, 409. */
    expectedRunDate: DateOnlySchema,
    reason: RescheduleReasonSchema,
    /** Solo calcula lo que pasaría (para enseñarlo antes de confirmar): no escribe nada. */
    dryRun: z.boolean().optional(),
});
/** Lo que pasa con la iguala al moverla (se calcula igual en la vista previa y al aplicarlo). */
export const RescheduleRecurringPreviewSchema = z.object({
    dryRun: z.boolean(),
    /** El periodo de la cuota que se facturará en la nueva fecha («diciembre 2026»). */
    newPeriodLabel: z.string(),
    /** Cuotas programadas ANTES de la nueva fecha que dejan de facturarse (las del mismo periodo que la nueva no cuentan). */
    skippedCount: z.number().int(),
    skippedLabels: z.array(z.string()),
    /**
     * La iguala tiene un nº de cuotas LIMITADO (`maxOccurrences`): aplazar no pierde ninguna cuota, el contrato conserva las que le
     * quedan y su final se retrasa. Las «omitidas» son entonces periodos sin facturar, no cuotas perdidas.
     */
    limitedByCount: z.boolean(),
    /**
     * La cuota SIGUIENTE a la de la nueva fecha (la iguala vuelve a su día habitual), YYYY-MM-DD; `null` si la de la nueva fecha es
     * la ÚLTIMA del contrato (se agota el nº de cuotas o la siguiente pasaría del fin).
     */
    followingRunDate: z.string().nullable(),
});
export const RescheduleRecurringResponseSchema = RescheduleRecurringPreviewSchema.extend({
    /** La iguala ya movida; ausente en la vista previa. */
    recurring: RecurringInvoiceDtoSchema.optional(),
});
//# sourceMappingURL=reschedule.js.map